import React, { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";

/**
 * RaceDataTransfer — export / réimport des données d'une course, pour
 * préparer rapidement une autre course (ex: doubles courses FIS : même
 * lieu, même jury, même piste, autre codex/date). Ajouté le 01.10.2026.
 *
 * Export : un fichier JSON avec la config de la course (Course & exports),
 * les participants et la config du Live FIS.
 * Import : depuis ce fichier OU directement depuis une autre course du
 * serveur (par son identifiant), en choisissant les parties à reprendre.
 * Les données reprises arrivent dans le formulaire, à vérifier puis
 * enregistrer ; participants et Live FIS sont enregistrés tout de suite
 * (après confirmation).
 */

const FORMAT = "321-timing-race-data";

// Parties reprenables -> champs de la config. `default` = coché par défaut
// (pensé pour une double course : codex/date et infos de manche changent).
const SECTIONS = [
    {
        key: "header",
        label: "En-tête : type, secteur, event, lieu, nation, discipline, genre, catégorie FIS / RTyp",
        fields: ["raceType", "sector", "event", "place", "nation", "discipline", "gender", "fisCategory", "rtyp", "category", "timing", "dataprocessing"],
        default: true,
    },
    { key: "codexDate", label: "Codex et date (différents pour une 2e course)", fields: ["codex", "date", "season", "seasonManual"], default: false },
    { key: "jury", label: "Jury", fields: ["jury"], default: true },
    { key: "technical", label: "Piste / données techniques (homologation, altitudes, longueur)", fields: ["technical"], default: true },
    { key: "runs", label: "Infos par manche (traceurs, ouvreurs, horaires, conditions)", fields: ["runs"], default: false },
    { key: "branding", label: "Impression : logos, bandeau, titre, pied de page", fields: ["bannerImage", "logoLeft", "logoRight", "raceTitle", "subTitle", "footerText", "footerLogo"], default: true },
    { key: "categories", label: "Catégories (ordre et règles)", fields: ["categories"], default: true },
    { key: "lists", label: "Listes de points utilisées (PList Swiss-Ski, liste FIS)", fields: ["plist", "usedFisList"], default: true },
];

export default function RaceDataTransfer({ eventKey, config, setConfig, apiFetch, onMessage }) {
    const [open, setOpen] = useState(false);
    const [source, setSource] = useState("file"); // "file" | "event"
    const [sourceKey, setSourceKey] = useState("");
    const [data, setData] = useState(null); // contenu chargé (fichier ou autre course)
    const [selected, setSelected] = useState(Object.fromEntries(SECTIONS.map((s) => [s.key, s.default])));
    const [withAthletes, setWithAthletes] = useState(false);
    const [withFisLive, setWithFisLive] = useState(true);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const getJson = async (url) => {
        const res = await apiFetch(url);
        if (!res || !res.ok) return null;
        return res.json().catch(() => null);
    };

    // Tout ce qui concerne une course, lu sur le serveur.
    const loadRace = async (key) => {
        const k = encodeURIComponent(key);
        const [cfg, athletes, fisLive] = await Promise.all([
            getJson(`/api/timing/swissski-config/${k}`),
            getJson(`/api/timing/athletes/${k}`),
            getJson(`/api/timing/fis-config/${k}`),
        ]);
        return { format: FORMAT, version: 1, sourceEventKey: key, exportedAt: new Date().toISOString(), config: cfg, athletes: athletes || [], fisLive };
    };

    // ---------- Export ----------
    const exportData = async () => {
        setBusy(true);
        try {
            const payload = await loadRace(eventKey);
            // Config telle qu'affichée (y compris modifications pas encore enregistrées).
            payload.config = { ...(payload.config || {}), ...config };
            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `course-${config.codex || eventKey}-${(config.date || "").replace(/\./g, "-") || "donnees"}.json`;
            a.click();
            URL.revokeObjectURL(url);
            onMessage?.("success", "Données de la course exportées ✓");
        } finally {
            setBusy(false);
        }
    };

    // ---------- Import ----------
    const openImport = () => {
        setData(null);
        setError("");
        setOpen(true);
    };

    const readFile = async (e) => {
        const file = e.target.files[0];
        e.target.value = "";
        if (!file) return;
        setError("");
        try {
            const parsed = JSON.parse(await file.text());
            if (parsed.format !== FORMAT || !parsed.config) throw new Error("ce n'est pas un export de course 321-timing.");
            setData(parsed);
            setWithFisLive(!!parsed.fisLive);
        } catch (err) {
            setData(null);
            setError(`Fichier illisible : ${err.message}`);
        }
    };

    const loadFromEvent = async () => {
        const key = sourceKey.trim();
        if (!key) return;
        if (key === eventKey) return setError("C'est la course actuelle.");
        setError("");
        setBusy(true);
        try {
            const d = await loadRace(key);
            if (!d.config) throw new Error(`aucune donnée trouvée pour « ${key} » (vérifier l'identifiant).`);
            setData(d);
            setWithFisLive(!!d.fisLive);
        } catch (err) {
            setData(null);
            setError(`Lecture impossible : ${err.message}`);
        } finally {
            setBusy(false);
        }
    };

    const apply = async () => {
        if (!data) return;
        setBusy(true);
        setError("");
        const done = [];
        try {
            // 1. Config : seulement les parties cochées, dans le formulaire.
            const patch = {};
            SECTIONS.filter((s) => selected[s.key]).forEach((s) =>
                s.fields.forEach((f) => {
                    if (data.config[f] !== undefined) patch[f] = data.config[f];
                })
            );
            if (Object.keys(patch).length) {
                setConfig((prev) => ({ ...prev, ...patch }));
                done.push(`${SECTIONS.filter((s) => selected[s.key]).length} partie(s) de la course`);
            }

            const k = encodeURIComponent(eventKey);

            // 2. Participants (remplacent la liste actuelle).
            if (withAthletes && (data.athletes || []).length) {
                const current = await getJson(`/api/timing/athletes/${k}`);
                if (
                    (current || []).length &&
                    !window.confirm(`Remplacer les ${current.length} participant(s) actuels par les ${data.athletes.length} de la source ?`)
                ) {
                    // on continue sans les participants
                } else {
                    const res = await apiFetch(`/api/timing/athletes/import/${k}`, { method: "POST", body: JSON.stringify(data.athletes) });
                    if (!res || !res.ok) throw new Error("enregistrement des participants impossible.");
                    done.push(`${data.athletes.length} participant(s)`);
                }
            }

            // 3. Live FIS : serveur, mot de passe, mode, inters, raceinfo —
            //    jamais le codex (propre à chaque course).
            if (withFisLive && data.fisLive) {
                const current = (await getJson(`/api/timing/fis-config/${k}`)) || {};
                const codex = current.codex || (selected.codexDate ? data.config.codex : config.codex) || "";
                if (!codex) {
                    done.push("Live FIS non repris (saisir d'abord le codex de cette course)");
                } else {
                    const { codex: _ignored, ...rest } = data.fisLive;
                    const res = await apiFetch(`/api/timing/fis-config/${k}`, {
                        method: "POST",
                        body: JSON.stringify({ ...current, ...rest, codex, raceinfo: { ...(rest.raceinfo || {}) } }),
                    });
                    if (!res || !res.ok) throw new Error("enregistrement de la config Live FIS impossible.");
                    done.push("config Live FIS");
                }
            }

            onMessage?.(
                "success",
                `Repris depuis « ${data.sourceEventKey || "fichier"} » : ${done.join(", ") || "rien"}. ` +
                (Object.keys(patch).length ? "Vérifiez le formulaire puis cliquez « Enregistrer »." : "")
            );
            setOpen(false);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const src = data?.config || {};

    return (
        <>
            <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportData} disabled={busy}>
                Exporter les données
            </Button>
            <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={openImport}>
                Reprendre d'une autre course
            </Button>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Reprendre les données d'une autre course</DialogTitle>
                <DialogContent>
                    <ToggleButtonGroup exclusive size="small" value={source} onChange={(_, v) => v && (setSource(v), setData(null))} sx={{ mb: 2 }}>
                        <ToggleButton value="file">Depuis un fichier exporté</ToggleButton>
                        <ToggleButton value="event">Depuis une course du serveur</ToggleButton>
                    </ToggleButtonGroup>

                    {source === "file" ? (
                        <Box>
                            <Button component="label" variant="outlined" startIcon={<FileUploadIcon />}>
                                Choisir le fichier (.json)
                                <input type="file" accept=".json,application/json" hidden onChange={readFile} />
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <TextField
                                size="small"
                                label="Identifiant de la course source"
                                placeholder="ex: FIS_Veysonnaz_GS1"
                                value={sourceKey}
                                onChange={(e) => setSourceKey(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && loadFromEvent()}
                                sx={{ minWidth: 320 }}
                            />
                            <Button variant="outlined" onClick={loadFromEvent} disabled={busy || !sourceKey.trim()}>
                                Charger
                            </Button>
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {data && (
                        <>
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Source : <b>{data.sourceEventKey || "fichier"}</b> — {src.event || "—"}, {src.place || "—"},{" "}
                                {src.discipline || "—"} {src.gender || ""}, codex {src.codex || "—"} du {src.date || "—"}
                                {data.exportedAt ? ` (exporté le ${new Date(data.exportedAt).toLocaleString("fr-CH")})` : ""}
                            </Alert>

                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                                Parties de la course à reprendre (dans le formulaire)
                            </Typography>
                            {SECTIONS.map((s) => (
                                <FormControlLabel
                                    key={s.key}
                                    sx={{ display: "block" }}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={!!selected[s.key]}
                                            onChange={(e) => setSelected((prev) => ({ ...prev, [s.key]: e.target.checked }))}
                                        />
                                    }
                                    label={s.label}
                                />
                            ))}

                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                Enregistré directement
                            </Typography>
                            <FormControlLabel
                                sx={{ display: "block" }}
                                control={<Checkbox size="small" checked={withAthletes} onChange={(e) => setWithAthletes(e.target.checked)} />}
                                label={`Participants (${(data.athletes || []).length}) — remplacent la liste actuelle`}
                            />
                            <FormControlLabel
                                sx={{ display: "block" }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={withFisLive && !!data.fisLive}
                                        disabled={!data.fisLive}
                                        onChange={(e) => setWithFisLive(e.target.checked)}
                                    />
                                }
                                label={
                                    data.fisLive
                                        ? "Config Live FIS (serveur, mot de passe, mode, inters, raceinfo) — sans le codex"
                                        : "Pas de config Live FIS dans la source"
                                }
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Annuler</Button>
                    <Button variant="contained" onClick={apply} disabled={!data || busy}>
                        Reprendre
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}